export type CourierHistoryStatus =
  | 'ok'
  | 'no_history'
  | 'not_configured'
  | 'inactive'
  | 'auth_failed'
  | 'rate_limited'
  | 'unavailable';

export interface CourierHistoryData {
  status?: CourierHistoryStatus;
  total?: number;
  delivery?: number;
  cancel?: number;
  /** Legacy delivered count, not a success percentage. */
  success?: number;
  error?: string;
  rate_limited?: boolean;
}

export interface CustomerIntelligenceData {
  fraud_check?: {
    steadfast?: CourierHistoryData;
    pathao?: CourierHistoryData;
    [key: string]: CourierHistoryData | undefined;
  };
  store_history?: {
    total_orders?: number;
    total_spent?: string | number;
    cancelled_orders?: number;
  };
  store_order_count?: number;
  store_orders?: Array<{
    order_number?: string;
    total_amount?: string | number;
    status?: string;
    created_at?: string;
  }>;
}

export const normalizeCustomerPhone = (phone: string): string => {
  let digits = String(phone || '').replace(/[^0-9]/g, '');
  if (digits.startsWith('880')) {
    digits = digits.slice(2);
  } else if (digits.startsWith('88')) {
    digits = digits.slice(2);
  }
  if (digits.length === 10 && digits.startsWith('1')) {
    digits = '0' + digits;
  }
  return digits;
};

export const isValidCustomerPhone = (phone: string) => /^01[3-9][0-9]{8}$/.test(normalizeCustomerPhone(phone));

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

export function getCourierStatistics(data?: CourierHistoryData) {
  if (!data) return null;
  const total = data.total;
  const delivered = data.delivery ?? data.success;
  const cancelled = data.cancel;
  if (!isCount(total) || !isCount(delivered) || !isCount(cancelled)) {
    return null;
  }
  // Only complete, consistent history can establish rates or a new phone.
  if (total !== delivered + cancelled) return null;
  const deliveryRate = total > 0 ? Math.round((delivered / total) * 100) : null;
  return {
    total,
    delivered,
    cancelled,
    deliveryRate,
    returnRate: total > 0 ? Math.round((cancelled / total) * 100) : null,
    isHighRisk: total >= 3 && cancelled / total > 0.5,
    isTrusted: total >= 2 && (deliveryRate ?? 0) >= 80,
  };
}

const statusMessages: Record<Exclude<CourierHistoryStatus, 'ok' | 'no_history'>, string> = {
  not_configured: 'Add courier portal login credentials in Integration Settings.',
  inactive: 'The courier integration is inactive. Enable it in Integration Settings.',
  auth_failed: 'Courier portal login failed. Check the credentials in Integration Settings.',
  rate_limited: 'The courier search limit has been reached. Try again after the limit resets.',
  unavailable: 'Courier delivery history is unavailable. Refresh intelligence to retry.',
};

export function getCourierIntelligence(data?: CourierHistoryData) {
  let status = data?.status;
  if (data?.error || data?.rate_limited || (status && status !== 'ok' && status !== 'no_history')) {
    if (!status || status === 'ok' || status === 'no_history') {
      status = data?.rate_limited ? 'rate_limited'
        : data?.error?.includes('not configured') ? 'not_configured'
          : 'unavailable';
    }
    return { status, statistics: null, message: data?.error || statusMessages[status] || statusMessages.unavailable };
  }

  const statistics = getCourierStatistics(data);
  if (!statistics || (status === 'no_history' && statistics.total !== 0)) {
    return { status: 'unavailable' as const, statistics: null, message: statusMessages.unavailable };
  }
  return {
    status: statistics.total === 0 ? 'no_history' as const : 'ok' as const,
    statistics,
    message: '',
  };
}
